import React, { useState } from 'react';
import { Step } from 'react-joyride';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues } from 'formik';
import { isEmpty } from 'lodash';
import * as yup from 'yup';

import ListPreview, { PreviewItem } from 'components/AggregationCards/ListPreview';
import Header from 'components/AppBar';
import DeleteButton from 'components/Buttons/DeleteButton';
import SubmitButton from 'components/Buttons/SubmitButton';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import Field from 'components/Field';
import FormikSubmitErrorEffect from 'components/FormikSubmitErrorEffect';
import Input from 'components/Input';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Section from 'components/Section';
import Tour from 'components/Tour';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Group } from 'models/group';
import { GroupedListModel } from 'models/groupedList';
import { RuleForm, RuleResponse } from 'models/rule';
import { SpecificDestination as SpecificDestinationModel } from 'models/specificDestination';
import { SpecificRent as SpecificRentModel } from 'models/specificRent';
import { SpecificRoute as SpecificRouteModel } from 'models/specificRoute';
import ResponseScreen, { BackgroundTypes } from 'screens/components/ResponseScreen';

import Groups from './components/Groups';
import PreviewItems from './components/PreviewItems';
import PreviewItemsDestination from './components/PreviewItems/PreviewItemsDestination';
import PreviewItemsVehicle from './components/PreviewItems/PreviewItemsVehicle';
import AutoRestriction, { Type as AutoRestrictionType } from './components/Restriction/Auto';
import FlightRestriction, { Type as FlightRestrictionType } from './components/Restriction/Flight';
import HotelRestriction, { Type as HotelRestrictionType } from './components/Restriction/Hotel';
import SpecificDestination from './components/Specific/Destination';
import SpecificList from './components/Specific/List/SpecificList';
import SpecificRent from './components/Specific/Rent';
import SpecificRoute from './components/Specific/Route';
import { Scaffold, Body, Form, Division, FormBody } from './styles';

interface Props {
  rule?: RuleResponse;
  originGroup?: string;
  groupList: Group[];
  saving: boolean;
  loading: boolean;
  countryCurrency: string;
  countryCulture: string;
  isFromSelection?: boolean;
  onSave: (data: RuleForm) => void;
  onDelete: (data: RuleForm) => void;
  onCloseFormRule?: () => void;
  showSuccess: boolean;
}

type SetFieldValue = (field: string, value: unknown) => void;

const Rule = ({
  rule,
  originGroup,
  groupList,
  saving,
  isFromSelection,
  onCloseFormRule,
  loading,
  countryCurrency,
  countryCulture,
  onSave,
  onDelete,
  showSuccess,
}: Props) => {
  const history = useHistory();
  const { t } = useStoreTranslation();

  const [showSelectionGroups, setShowSelectionGroups] = useState(false);
  const [showSpecificRoute, setShowSpecificRoute] = useState(false);
  const [showSpecificDestination, setShowSpecificDestination] = useState(false);
  const [showSpecificRent, setShowSpecificRent] = useState(false);
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<GroupedListModel[]>([]);
  const [showSpecificRouteList, setShowSpecificRouteList] = useState(false);
  const [showSpecificDestinationList, setShowSpecificDestinationList] = useState(false);
  const [showSpecificRentList, setShowSpecificRentList] = useState(false);

  const getSpecificRents = (rule?: RuleResponse): SpecificRentModel[] => {
    const rules: SpecificRentModel[] = [];
    rule?.conditions?.specificVehicleLocations?.forEach((location) => {
      if (!isEmpty(location))
        rules.push({
          pickup: {
            code: `${location.pickup?.code}`,
            name: `${location.pickup?.name}`,
            latitude: location.pickup?.latitude,
            longitude: location.pickup?.longitude,
          },
          dropoff: {
            code: `${location.dropoff?.code}`,
            name: `${location.dropoff?.name}`,
            latitude: location.dropoff?.latitude,
            longitude: location.dropoff?.longitude,
          },
          maxPrice: location.valueVehicle.value,
          dateVehicle: location.dateVehicle.days,
        } as SpecificRentModel);
    });
    return rules;
  };

  const initialValues: RuleForm = {
    name: rule?.name || '',
    nationalFlightAnticipation: rule?.conditions?.dateAirDomestic?.days || '',
    nationalFlightMaxPrice: rule?.conditions?.valueAirDomestic?.value || '',
    nationalFlightFlexibility: rule?.conditions?.percentageLlfAirDomestic?.value || '',
    internationalFlightAnticipation: rule?.conditions?.dateAirInternational?.days || '',
    internationalFlightMaxPrice: rule?.conditions?.valueAirInternational?.value || '',
    internationalFlightFlexibility: rule?.conditions?.percentageLlfAirInternational?.value || '',
    nationalHotelAnticipation: rule?.conditions?.dateHotelDomestic?.days || '',
    nationalHotelMaxPrice: rule?.conditions?.valueHotelDomestic?.value || '',
    internationalHotelAnticipation: rule?.conditions?.dateHotelInternational?.days || '',
    internationalHotelMaxPrice: rule?.conditions?.valueHotelInternational?.value || '',
    nationalVehicleAnticipation: rule?.conditions?.dateVehicleDomestic?.days || '',
    nationalVehicleMaxPrice: rule?.conditions?.valueVehicleDomestic?.value || '',
    internationalVehicleAnticipation: rule?.conditions?.dateVehicleInternational?.days || '',
    internationalVehicleMaxPrice: rule?.conditions?.valueVehicleInternational?.value || '',
    groups: rule?.userGroupIds || [],
    specificRoutes:
      rule?.conditions?.specificAirRoutes?.map((route) => ({
        departurePointCode: route.origin,
        departurePoint: route.originName,
        arrivalPoint: route.destinyName,
        arrivalPointCode: route.destiny,
        maxPrice: route.valueAir?.value,
        specificFlightAnticipationDays: route.dateAir.days,
        specificFlightFlexibility: route.percentageLlfAir?.value,
      })) || [],
    specificDestinations:
      rule?.conditions?.specificHotelLocations?.map((destination) => ({
        destinationPoint: {
          code: destination.location,
          name: destination.locationName,
        },
        maxPrice: destination.valueHotel ? destination.valueHotel.value : 0,
        dateHotel: destination.valueHotel ? destination.dateHotel.days : 0,
      })) || [],
    specificRents: getSpecificRents(rule),
  };

  const validationSchema = yup.object().shape({
    name: yup.string().required(),
    groups: yup.array(),

    nationalFlightAnticipation: yup.number().min(0),
    nationalFlightMaxPrice: yup.number(),
    nationalFlightFlexibility: yup.number().min(0).max(100),
    internationalFlightAnticipation: yup.number().min(0),
    internationalFlightMaxPrice: yup.number(),
    internationalFlightFlexibility: yup.number().min(0).max(100),
    nationalHotelAnticipation: yup.number().min(0),
    nationalHotelMaxPrice: yup.number(),
    internationalHotelAnticipation: yup.number().min(0),
    internationalHotelMaxPrice: yup.number(),
    nationalVehicleAnticipation: yup.number().min(0),
    nationalVehicleMaxPrice: yup.number(),
    internationalVehicleAnticipation: yup.number().min(0),
    internationalVehicleMaxPrice: yup.number(),

    specificRoutes: yup.array(),
    specificDestinations: yup.array(),
    specificRents: yup.array(),
  });

  const turnGroupsIntoPreviewItem = (currentGroupsId: string[]) => {
    const groupsPreview: PreviewItem[] = [];

    if (groupList.length > 0) {
      if (currentGroupsId.length > 0) {
        const currentGroups: Group[] = currentGroupsId
          .map((groupId) => groupList.find((group) => group.id === groupId) as Group)
          .filter((group) => group !== undefined);

        if (currentGroups.length > 0) {
          currentGroups.forEach((group) => {
            if (group !== undefined) groupsPreview.push({ title: group?.name });
          });
        }
      }
    }
    return groupsPreview;
  };

  const turnSpecificRoutesIntoPreviewItem = (specificRoutes: SpecificRouteModel[]) => {
    const specificRoutesPreview: PreviewItem[] = [];
    specificRoutes.map((specificRoute) =>
      specificRoutesPreview.push({
        title: specificRoute.arrivalPoint || '',
      }),
    );
    return specificRoutesPreview;
  };

  const turnSpecificDestinationsIntoPreviewItem = (specificDestinations: SpecificDestinationModel[]) => {
    const specificDestinationsPreview: PreviewItem[] = [];
    specificDestinations.map((specificDestination) =>
      specificDestinationsPreview.push({
        title: specificDestination.destinationPoint ? specificDestination.destinationPoint.name : '',
      }),
    );
    return specificDestinationsPreview;
  };

  const turnSpecificRentsIntoPreviewItem = (specificRents: SpecificRentModel[]) => {
    const specificRentsPreview: PreviewItem[] = [];
    specificRents.map((specificRent) =>
      specificRentsPreview.push({
        title: specificRent.pickup ? specificRent.pickup.name : '',
        subtitle: specificRent.dropoff ? specificRent.dropoff.name : '',
      }),
    );
    return specificRentsPreview;
  };

  const handleConfirmSelectionGroups = (groups: GroupedListModel[], setFieldValue: SetFieldValue) => {
    setCurrentGroup(groups);
    setFieldValue(
      'groups',
      groups.map((group) => group.id),
    );
  };

  const handleConfirmSpecificRoute = (
    route: SpecificRouteModel,
    values: typeof initialValues,
    setFieldValue: SetFieldValue,
  ) => {
    setFieldValue('specificRoutes', [
      ...values.specificRoutes.filter(
        (value) => value.departurePoint !== route.departurePoint || value.arrivalPoint !== route.arrivalPoint,
      ),
      route,
    ]);
  };

  const handleRemoveSpecificRoute = (
    route: SpecificRouteModel[],
    values: typeof initialValues,
    setFieldValue: SetFieldValue,
  ) => {
    setFieldValue(
      'specificRoutes',
      values.specificRoutes.filter(
        (value) =>
          !route.find((r) => value.departurePoint === r.departurePoint && value.arrivalPoint === r.arrivalPoint),
      ),
    );
  };

  const handleConfirmSpecificDestination = (
    destination: SpecificDestinationModel,
    values: typeof initialValues,
    setFieldValue: SetFieldValue,
  ) => {
    setFieldValue('specificDestinations', [
      ...values.specificDestinations.filter(
        (value) => value.destinationPoint?.code !== destination.destinationPoint?.code,
      ),
      destination,
    ]);
  };

  const handleRemoveSpecificDestination = (
    destination: SpecificDestinationModel[],
    values: typeof initialValues,
    setFieldValue: SetFieldValue,
  ) => {
    setFieldValue(
      'specificDestinations',
      values.specificDestinations.filter(
        (value) => !destination.find((d) => value.destinationPoint?.code === d.destinationPoint?.code),
      ),
    );
  };

  const handleConfirmSpecificRent = (
    pickup: SpecificRentModel,
    values: typeof initialValues,
    setFieldValue: SetFieldValue,
  ) => {
    setFieldValue('specificRents', [
      ...values.specificRents.filter((value) => value.pickup?.code !== pickup.pickup?.code),
      pickup,
    ]);
  };

  const handleRemoveSpecificRent = (
    pickup: SpecificRentModel[],
    values: typeof initialValues,
    setFieldValue: SetFieldValue,
  ) => {
    setFieldValue(
      'specificRents',
      values.specificRents.filter((value) => !pickup.find((d) => value.pickup?.code === d.pickup?.code)),
    );
  };

  const handleSubmit = (data: RuleForm) => {
    if (!rule) {
      onSave(data);
    } else {
      setShowConfirmEdit(true);
    }
  };

  const initialSteps: Step[] = [
    {
      target: '---',
      content: '',
    },
    {
      target: '.target .target-flightMaxPrice',
      content: t('rule.tour.flightMaxPrice'),
      disableBeacon: true,
      placement: 'bottom',
      offset: 0,
    },
  ];

  const handleCloseTooltip = () => {
    setShowTooltip(false);
  };

  const handleNavigateHistory = () => {
    if (isFromSelection) {
      return onCloseFormRule && onCloseFormRule();
    }
    return history.push('/corporate/rules');
  };

  return (
    <>
      <Scaffold className="target">
        <Mobile>
          <Header goBack={handleNavigateHistory}>{t(!rule ? 'rule.createHeader' : 'rule.editHeader')}</Header>
        </Mobile>

        <Body>
          {loading ? (
            <Loader fullScreen />
          ) : (
            <>
              <Desktop>
                <ScreenHeader title={t('userMenu.rulesTitle')} subtitle={t('userMenu.rulesDetail')} />
              </Desktop>
              <Formik
                initialValues={initialValues}
                isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, setFieldValue, dirty }: FormikValues) => (
                  <>
                    <FormikSubmitErrorEffect />

                    <Form>
                      <Tour
                        type="information"
                        show={showTooltip}
                        steps={initialSteps}
                        hideArrow={false}
                        onClose={handleCloseTooltip}
                      />
                      <FormBody padding={rule && dirty}>
                        <Section title={t('rule.title')}>
                          <Field name="name" labelKey="rule.name">
                            <Input placeholder={t('rule.namePlaceHolder')} />
                          </Field>
                        </Section>

                        <Division />

                        <Groups
                          show={showSelectionGroups}
                          isFromSelection={isFromSelection}
                          originGroup={originGroup}
                          currentGroup={currentGroup}
                          selectedGroupsRules={values.groups}
                          groupTarget={values.name}
                          previewItems={turnGroupsIntoPreviewItem(values.groups)}
                          onConfirmSelection={(group: GroupedListModel[]) =>
                            handleConfirmSelectionGroups(group, setFieldValue)
                          }
                          onCloseModal={() => setShowSelectionGroups(false)}
                          onAdd={() => setShowSelectionGroups(true)}
                        />

                        <Division />

                        <Section title={t('rule.flights')}>
                          <FlightRestriction type={FlightRestrictionType.National} isTourTooltip />
                          <FlightRestriction type={FlightRestrictionType.International} isTourTooltip />
                          <SpecificList
                            isRoutes
                            show={showSpecificRouteList}
                            items={values.specificRoutes}
                            countryCurrency={countryCurrency}
                            countryCulture={countryCulture}
                            onCloseModal={() => setShowSpecificRouteList(false)}
                            onOpenSpecificRoute={() => setShowSpecificRoute(true)}
                            onRemove={(route: SpecificRouteModel[]) =>
                              handleRemoveSpecificRoute(route, values, setFieldValue)
                            }
                          />
                          <Field name="specificRoutes" labelKey="rule.exceptionLabel">
                            <ListPreview
                              icon="flight"
                              placeholder={t('rule.exceptionPlaceholder')}
                              onClick={() => setShowSpecificRouteList(true)}
                              onAdd={() => setShowSpecificRoute(true)}
                              previewItems={turnSpecificRoutesIntoPreviewItem(values.specificRoutes)}
                            >
                              <PreviewItems values={values.specificRoutes} countryCurrency={countryCurrency} />
                            </ListPreview>
                          </Field>
                        </Section>
                        <Division />
                        <Section title={t('rule.hotels')}>
                          <HotelRestriction type={HotelRestrictionType.National} />
                          <HotelRestriction type={HotelRestrictionType.International} />
                          <SpecificList
                            show={showSpecificDestinationList}
                            items={values.specificDestinations}
                            countryCurrency={countryCurrency}
                            countryCulture={countryCulture}
                            onCloseModal={() => setShowSpecificDestinationList(false)}
                            onOpenSpecificRoute={() => setShowSpecificDestination(true)}
                            onRemove={(destination: SpecificDestinationModel[]) =>
                              handleRemoveSpecificDestination(destination, values, setFieldValue)
                            }
                          />
                          <Field name="specificDestinations" labelKey="rule.exceptionLabel">
                            <ListPreview
                              icon="hotel"
                              placeholder={t('rule.exceptionPlaceholder')}
                              onClick={() => setShowSpecificDestinationList(true)}
                              onAdd={() => setShowSpecificDestination(true)}
                              previewItems={turnSpecificDestinationsIntoPreviewItem(values.specificDestinations)}
                            >
                              <PreviewItemsDestination
                                values={values.specificDestinations}
                                countryCurrency={countryCurrency}
                              />
                            </ListPreview>
                          </Field>
                        </Section>
                        <Division />
                        <Section title={t('rule.autos')}>
                          <AutoRestriction type={AutoRestrictionType.National} />
                          <AutoRestriction type={AutoRestrictionType.International} />
                          <SpecificList
                            show={showSpecificRentList}
                            items={values.specificRents}
                            countryCurrency={countryCurrency}
                            countryCulture={countryCulture}
                            onCloseModal={() => setShowSpecificRentList(false)}
                            onOpenSpecificRoute={() => setShowSpecificRent(true)}
                            onRemove={(pickUp: SpecificRentModel[]) =>
                              handleRemoveSpecificRent(pickUp, values, setFieldValue)
                            }
                          />
                          <Field name="specificRents" labelKey="rule.exceptionLabel">
                            <ListPreview
                              icon="auto"
                              placeholder={t('rule.exceptionPlaceholder')}
                              onClick={() => setShowSpecificRentList(true)}
                              onAdd={() => setShowSpecificRent(true)}
                              previewItems={turnSpecificRentsIntoPreviewItem(values.specificRents)}
                            >
                              <PreviewItemsVehicle values={values.specificRents} countryCurrency={countryCurrency} />
                            </ListPreview>
                          </Field>
                        </Section>

                        {rule && (
                          <DeleteButton
                            value={t('rule.delete.buttonValue')}
                            onClick={() => setShowConfirmDelete(true)}
                            isEdition={!!rule}
                          />
                        )}
                        <ConfirmBottomSheet
                          show={showConfirmDelete}
                          title={t('rule.delete.bottomSheetTitle')}
                          onClose={() => setShowConfirmDelete(false)}
                          content={t('rule.delete.bottomSheetContent')}
                          confirmText={t('rule.delete.bottomSheetButton')}
                          onConfirm={() => onDelete(values)}
                        />
                      </FormBody>

                      <SubmitButton isEdit={!!rule} dirty={dirty} loading={saving}>
                        {!rule ? t('rule.createButton') : t('rule.saveButton')}
                      </SubmitButton>
                      <ConfirmBottomSheet
                        show={showConfirmEdit}
                        title={t('rule.editConfirmTitle')}
                        content={t('rule.editConfirmText')}
                        onConfirm={() => onSave(values)}
                        onClose={() => setShowConfirmEdit(false)}
                      />
                    </Form>
                    <SpecificDestination
                      show={showSpecificDestination}
                      onClose={() => setShowSpecificDestination(false)}
                      onConfirm={(destination: SpecificDestinationModel) =>
                        handleConfirmSpecificDestination(destination, values, setFieldValue)
                      }
                    />
                    <SpecificRent
                      show={showSpecificRent}
                      onClose={() => setShowSpecificRent(false)}
                      onConfirm={(pickUp: SpecificRentModel) =>
                        handleConfirmSpecificRent(pickUp, values, setFieldValue)
                      }
                    />
                    <SpecificRoute
                      show={showSpecificRoute}
                      onClose={() => setShowSpecificRoute(false)}
                      onConfirm={(route: SpecificRouteModel) =>
                        handleConfirmSpecificRoute(route, values, setFieldValue)
                      }
                    />
                  </>
                )}
              </Formik>
            </>
          )}
        </Body>
      </Scaffold>

      <ResponseScreen
        show={showSuccess}
        text={t('rulesComponent.rulesCreationSuccess')}
        subText={t('rulesComponent.rulesCreationText')}
        buttonText={t('rulesComponent.continue')}
        background={BackgroundTypes.Mountain}
        buttonOnClick={() => history.push('/corporate/rules')}
      />
    </>
  );
};

export default Rule;
